import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { paymentsAPI } from '../../services/api';
import { toast } from 'sonner';
import { CheckCircle, ArrowRight, CreditCard, AlertTriangle, Shield } from 'lucide-react';

const PaymentAuthorized = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [dealId, setDealId] = useState(null);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      confirmAuthorization();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const confirmAuthorization = async () => {
    try {
      const response = await paymentsAPI.confirmAuthorization(sessionId);
      setDealId(response.data.deal_id);
      setStatus('success');
      toast.success('Payment method authorized successfully!');
    } catch (error) {
      console.error('Authorization error:', error);
      setStatus('error');
      toast.error(error.response?.data?.detail || 'Failed to confirm authorization');
    }
  };

  if (status === 'loading') {
    return (
      <Layout title="Confirming Authorization">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Confirming your payment authorization...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (status === 'error') {
    return (
      <Layout title="Authorization Error">
        <Card className="max-w-lg mx-auto glass-effect border-red-200 dark:border-red-800">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              Authorization Failed
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              We couldn't confirm your payment authorization. Please try again.
            </p>
            <Button onClick={() => navigate('/borrower')} className="gradient-primary border-0">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout title="Payment Authorized">
      <Card className="max-w-2xl mx-auto glass-effect border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
        <CardContent className="py-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          
          <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            Payment Authorized!
          </h2>
          
          <div className="max-w-md mx-auto space-y-4 mb-8">
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Your payment method has been securely saved.
            </p>
            
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 text-left">
              <div className="flex items-start gap-3">
                <CreditCard className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">$750 Intake Fee Authorized</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    You will <strong>only be charged</strong> when an LSP commits to your deal.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800 text-left">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">What happens next?</p>
                  <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                    <li>• Your deal will be reviewed by our admin team</li>
                    <li>• Once approved, it will appear in the LSP marketplace</li>
                    <li>• When an LSP commits, you'll receive a notification</li>
                    <li>• The $750 fee will be charged at that time</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {dealId && (
              <Button 
                onClick={() => navigate(`/borrower/deals/${dealId}`)}
                className="gradient-primary border-0"
              >
                View Deal Details
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            <Button 
              onClick={() => navigate('/borrower')}
              variant="outline"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default PaymentAuthorized;
